import React, { useState, useRef } from 'react';
import { CircularProgress } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { toast } from 'react-toastify';
import { validateApplications } from './apihelpers';
import {
    parseCSVFile,
    parseExcelFile,
    parseCommaSeparatedInput,
    generateListName,
} from './apihelpers';

/**
 * SubscriptionListForm Component
 * Form for creating a new subscription list with validation
 */
const SubscriptionListForm = ({
    onCancel,
    onCreate,
    existingListsCount = 0,
    userName,
}) => {
    const [listName, setListName] = useState('');
    const [description, setDescription] = useState('');
    const [inputMethod, setInputMethod] = useState('paste'); // 'paste' or 'upload'
    const [bulkInput, setBulkInput] = useState('');
    const [uploadedFile, setUploadedFile] = useState(null);
    const [status, setStatus] = useState('idle'); // 'idle', 'validating', 'preview', 'error'
    const [validApps, setValidApps] = useState([]);
    const [invalidApps, setInvalidApps] = useState([]);
    const [errorMessage, setErrorMessage] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [showAllInvalidApps, setShowAllInvalidApps] = useState(false);

    const fileInputRef = useRef(null);

    const handleFileSelect = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;

        const validTypes = ['text/csv', 'application/vnd.ms-excel'];
        const validExtensions = ['.csv', '.xlsx', '.xls'];
        const hasValidExtension = validExtensions.some((ext) =>
            file.name.toLowerCase().endsWith(ext)
        );

        if (!validTypes.includes(file.type) && !hasValidExtension) {
            setErrorMessage('Invalid file type. Please upload a CSV or Excel file.');
            setStatus('error');
            return;
        }

        setUploadedFile(file);
        setErrorMessage('');
        setStatus('idle');
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
        setValidApps([]);
        setInvalidApps([]);
        setStatus('idle');
        setErrorMessage('');
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleValidate = async () => {
        setStatus('validating');
        setErrorMessage('');
        setValidApps([]);
        setInvalidApps([]);

        try {
            let appNames = [];

            if (inputMethod === 'paste') {
                appNames = parseCommaSeparatedInput(bulkInput);
            } else if (uploadedFile) {
                const fileName = uploadedFile.name.toLowerCase();
                if (fileName.endsWith('.csv')) {
                    appNames = await parseCSVFile(uploadedFile);
                } else if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
                    appNames = await parseExcelFile(uploadedFile);
                } else {
                    throw new Error('Unsupported file format');
                }
            }

            if (appNames.length === 0) {
                setErrorMessage(
                    inputMethod === 'paste'
                        ? 'Please enter CI names or IDs(MAC App ID) separated by commas.'
                        : 'No valid CI names found in the file.'
                );
                setStatus('error');
                return;
            }

            const response = await validateApplications(appNames);

            setValidApps(response.valid_applications || []);
            setInvalidApps(response.invalid_applications || []);
            setStatus('preview');

            if (response.invalid_applications?.length > 0) {
                toast.warning(
                    `${response.invalid_applications.length} CI(s) could not be validated`,
                    {
                        position: 'top-center',
                        autoClose: 5000,
                    }
                );
            }
        } catch (error) {
            setErrorMessage(error.message || 'Failed to validate Congiguration Items');
            setStatus('error');
            toast.error('Failed to validate Congiguration Items', {
                position: 'top-center',
                autoClose: 3000,
            });
        }
    };

    const handleCreate = async () => {
        if (validApps.length === 0) {
            setErrorMessage('Please add at least one valid configuration item.');
            return;
        }

        setIsCreating(true);

        try {
            // Use auto-generated name if not provided
            const finalListName =
                listName.trim() || generateListName(existingListsCount, userName);

            const validAppNames = validApps.map((app) => app.app_name);

            await onCreate({
                listName: finalListName,
                description: description.trim() || null,
                appNames: validAppNames,
                validApps,
            });

            toast.success(`Subscription list "${finalListName}" created successfully!`, {
                position: 'top-center',
                autoClose: 3000,
            });

            handleCancel();
        } catch (error) {
            toast.error(error.message || 'Failed to create subscription list', {
                position: 'top-center',
                autoClose: 3000,
            });
        } finally {
            setIsCreating(false);
        }
    };

    const handleCancel = () => {
        setListName('');
        setDescription('');
        setBulkInput('');
        setUploadedFile(null);
        setValidApps([]);
        setInvalidApps([]);
        setStatus('idle');
        setErrorMessage('');
        onCancel();
    };

    return (
        <div className="subscription-list-form">
            <div className="form-header">
                <h4>Create New Subscription List</h4>
                <button className="close-btn" onClick={handleCancel}>
                    <CloseIcon />
                </button>
            </div>

            {/* List Name */}
            <div className="form-group">
                <label>
                    List Name <span className="optional">(Optional)</span>
                </label>
                <input
                    type="text"
                    placeholder="Leave empty for auto-generated name"
                    value={listName}
                    onChange={(e) => setListName(e.target.value)}
                    className="form-input"
                />
            </div>

            {/* Description */}
            <div className="form-group">
                <label>
                    Description <span className="optional">(Optional)</span>
                </label>
                <textarea
                    placeholder="Enter a description for this list"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="form-textarea"
                    rows={2}
                />
            </div>

            {/* Input Method Toggle */}
            <div className="input-method-toggle">
                <button
                    className={`toggle-btn ${inputMethod === 'paste' ? 'active' : ''}`}
                    onClick={() => setInputMethod('paste')}
                >
                    Paste Values
                </button>
                <button
                    className={`toggle-btn ${inputMethod === 'upload' ? 'active' : ''}`}
                    onClick={() => setInputMethod('upload')}
                >
                    Upload File
                </button>
            </div>

            {/* Paste Input */}
            {inputMethod === 'paste' && (
                <div className="form-group">
                    <label>Configuration Items (comma-separated)</label>
                    <textarea
                        placeholder="Enter congiguration items separated by commas (e.g., APP001, APP002, COMET)"
                        value={bulkInput}
                        onChange={(e) => setBulkInput(e.target.value)}
                        className="form-textarea"
                        rows={4}
                    />
                    <button
                        className="animated-search-button validate-btn"
                        onClick={handleValidate}
                        disabled={status === 'validating' || !bulkInput.trim()}
                    >
                        {status === 'validating' ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : (
                            'Validate Configuration Items'
                        )}
                    </button>
                </div>
            )}

            {/* File Upload */}
            {inputMethod === 'upload' && (
                <div className="form-group">
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        onChange={handleFileSelect}
                        style={{ display: 'none' }}
                        id="subscription-list-file-upload"
                    />

                    {!uploadedFile ? (
                        <label htmlFor="subscription-list-file-upload" className="file-drop-zone">
                            <CloudUploadIcon className="upload-icon" />
                            <p className="upload-text">Click to upload CSV or Excel file</p>
                            <p className="upload-hint">One CI per row or comma-separated</p>
                        </label>
                    ) : (
                        <div className="uploaded-file">
                            <div className="file-info">
                                <InsertDriveFileIcon className="file-icon" />
                                <div>
                                    <p className="file-name">{uploadedFile.name}</p>
                                    <p className="file-size">
                                        {(uploadedFile.size / 1024).toFixed(2)} KB
                                    </p>
                                </div>
                            </div>
                            <button className="remove-file-btn" onClick={handleRemoveFile}>
                                <CloseIcon />
                            </button>
                        </div>
                    )}

                    {uploadedFile && (
                        <button
                            className="animated-search-button validate-btn"
                            onClick={handleValidate}
                            disabled={status === 'validating'}
                        >
                            {status === 'validating' ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                'Validate Configuration Items'
                            )}
                        </button>
                    )}
                </div>
            )}

            {/* Error Message */}
            {status === 'error' && errorMessage && (
                <div className="validation-error">
                    <ErrorIcon className="error-icon" />
                    <span>{errorMessage}</span>
                </div>
            )}

            {/* Validation Preview */}
            {status === 'preview' && (
                <div className="validation-preview">
                    <div className="preview-header">
                        <CheckCircleIcon className="success-icon" />
                        <span>
                            Validation Complete: {validApps.length} valid,{' '}
                            {invalidApps.length} invalid
                        </span>
                    </div>

                    {/* Valid Applications Table */}
                    {validApps.length > 0 && (
                        <div className="preview-section">
                            <h5>Valid CIs ({validApps.length})</h5>
                            <div className="preview-table-container">
                                <table className="preview-table">
                                    <thead>
                                        <tr>
                                            <th>CI Name</th>
                                            <th>CI ID</th>
                                            <th>Environment</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {validApps.map((app, index) => (
                                            <tr key={index}>
                                                <td>{app.app_name}</td>
                                                <td>{app.app_id}</td>
                                                <td>{app.environment}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Invalid Applications */}
                    {invalidApps.length > 0 && (
                        <div className="preview-section invalid">
                            <h5>Invalid CIs ({invalidApps.length})</h5>
                            <div className="invalid-apps-list">
                                {(showAllInvalidApps ? invalidApps : invalidApps.slice(0, 5)).map((app, index) => (
                                    <span key={index} className="invalid-app-chip">
                                        {app}
                                    </span>
                                ))}
                                {invalidApps.length > 5 && (
                                    <button
                                        className="show-more-btn"
                                        onClick={() => setShowAllInvalidApps(!showAllInvalidApps)}
                                        style={{
                                            display: 'inline-block',
                                            padding: '4px 10px',
                                            margin: '0',
                                            backgroundColor: 'transparent',
                                            border: '1px solid #42b0d5',
                                            color: '#42b0d5',
                                            borderRadius: '16px',
                                            cursor: 'pointer',
                                            fontSize: '12px',
                                            fontFamily: 'inherit',
                                            verticalAlign: 'middle',
                                            lineHeight: '1'
                                        }}
                                    >
                                        {showAllInvalidApps 
                                            ? 'Show Less' 
                                            : `+${invalidApps.length - 5} CI${invalidApps.length - 5 !== 1 ? 's' : ''} more`
                                        }
                                    </button>
                                )}
                            </div>
                            <p className="invalid-note">
                                These CIs will not be added to the list.
                            </p>
                        </div>
                    )}

                    {/* Create Button */}
                    <div className="form-actions">
                        <button
                            className="animated-search-button cancel-btn"
                            onClick={handleCancel}
                        >
                            Cancel
                        </button>
                        <button
                            className="animated-search-button create-btn"
                            onClick={handleCreate}
                            disabled={isCreating || validApps.length === 0}
                        >
                            {isCreating ? (
                                <CircularProgress size={16} color="inherit" />
                            ) : (
                                `Create List with ${validApps.length} CI${validApps.length !== 1 ? 's' : ''}`
                            )}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SubscriptionListForm;
